import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiOutlineHeart, AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai';
import { BsStarFill, BsStarHalf, BsStar, BsStars } from 'react-icons/bs';
import { FiTrash2, FiChevronRight } from 'react-icons/fi';
import { PulseLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../Header/Header';
import { fetchWishlist, removeItemFromWishlist } from '../../redux/thunks/wishlistThunks';
import { updateItemQuantity, addToCart, fetchCart } from '../../redux/thunks/cartThunks';
import fallbackImage from '../../assets/brand-default.jpg';
import Footer from '../../Components/Footer/Footer';

const WishlistPage = () => {
  const [removingId, setRemovingId] = useState(null);
  const [productRatings, setProductRatings] = useState({});
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { 
    items: wishlist, 
    loading: wishlistLoading, 
    error: wishlistError,
    initialized: wishlistInitialized
  } = useSelector(state => state.wishlist);
  
  const cartItems = useSelector(state => state.cart.items);
  const token = Cookies.get("ecom_user_token");

  useEffect(() => {
    if (!wishlistInitialized && !wishlistLoading) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, wishlistInitialized, wishlistLoading]);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  const fetchProductRatings = useCallback(async (productId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.ratings || [];
    } catch (error) {
      console.error(`Failed to fetch ratings for product ${productId}:`, error);
      return [];
    }
  }, [token]);

  useEffect(() => {
    const fetchAllRatings = async () => {
      if (wishlist.length > 0 && !ratingsLoading) {
        setRatingsLoading(true);
        try {
          const ratingsMap = {};
          for (const item of wishlist) {
            ratingsMap[item.productId._id] = await fetchProductRatings(item.productId._id);
          }
          setProductRatings(ratingsMap);
        } catch (error) {
          toast.error("Failed to load some ratings");
        } finally {
          setRatingsLoading(false);
        }
      }
    };

    fetchAllRatings();
  }, [wishlist, fetchProductRatings]);

  const calculateAverageRating = useCallback((ratings) => {
    if (!ratings || ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
    return sum / ratings.length;
  }, []);

  const handleAddToCart = async (product, variantId, e) => {
    e.stopPropagation();
    try {
      await dispatch(addToCart({
        productId: product._id,
        variantId: variantId,
        quantity: 1
      }));
      toast.success("Added to cart!");
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error(error.response?.data?.message || "Failed to add to cart");
    }
  };

  const handleQuantityChange = async (productId, variantId, newQuantity, e) => {
    e.stopPropagation();
    try {
      await dispatch(updateItemQuantity({ 
        productId, 
        variantId,
        quantity: newQuantity 
      }));
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("Failed to update quantity. Please try again.");
    }
  };

  const handleRemoveFromWishlist = async (productId, variantId, e) => {
    e.stopPropagation();
    const uniqueId = `${productId}-${variantId}`;
    try {
      setRemovingId(uniqueId);
      await dispatch(removeItemFromWishlist(productId, variantId));
      toast.success("Removed from wishlist");
    } catch (error) {
      toast.error(error.message || "Failed to remove item");
      console.error("Remove error:", error);
    } finally {
      setRemovingId(null);
    }
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  const getProductImage = (product, variant) => {
    if (imageErrors[product._id]) {
      return fallbackImage;
    }
    return variant?.images?.[0] || product.images?.[0] || fallbackImage;
  };

  const fetchProductDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
    }
    
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
    }
    
    return stars;
  };

  const renderProductActions = (product, variant) => {
    const inStock = variant ? variant.stock > 0 : product.stock > 0;
    const cartItem = cartItems.find(item => 
      item.productId === product._id && 
      (product.variants?.length > 0 ? item.variantId === variant?._id : true)
    );
    
    if (cartItem) {
      return (
        <div className="flex items-center justify-center space-x-2">
          <button
            className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 hover:text-gray-900 hover:shadow-md transition-all duration-200 shadow-sm ${
              cartItem.quantity <= 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={(e) => {
              const newQuantity = cartItem.quantity - 1;
              handleQuantityChange(product._id, variant?._id, newQuantity, e);
            }}
            disabled={cartItem.quantity <= 0}
          >
            <span className="text-lg font-medium">−</span>
          </button>
          <span className="w-8 text-center font-medium text-gray-700">
            {cartItem.quantity}
          </span>
          <button
            className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-b from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-blue-700 hover:text-blue-900 hover:shadow-md transition-all duration-200 shadow-sm ${
              !inStock ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={(e) => handleQuantityChange(product._id, variant?._id, cartItem.quantity + 1, e)}
            disabled={!inStock}
          >
            <span className="text-lg font-medium">+</span>
          </button>
        </div>
      );
    } else {
      return (
        <button 
          className={`w-full py-2 px-4 rounded-md bg-gradient-to-b from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-md transition-all duration-200 shadow-sm flex items-center justify-center ${
            !inStock ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={(e) => handleAddToCart(product, variant?._id, e)}
          disabled={!inStock}
        >
          <AiOutlineShoppingCart className="mr-2" />
          {inStock ? 'Add to Cart' : 'Out of Stock'}
        </button>
      );
    }
  };

  if (wishlistLoading && !wishlistInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <PulseLoader color="#3B82F6" size={15} />
      </div>
    );
  }

  if (wishlistError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Wishlist</h2>
          <p className="text-gray-600 mb-6">{wishlistError}</p>
          <button
            onClick={() => dispatch(fetchWishlist())}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header/>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Your Wishlist</h1>
              <p className="mt-2 text-sm text-gray-600">
                {wishlist.length} {wishlist.length === 1 ? 'item' : 'items'}
              </p>
            </div>
            <button 
              className="flex items-center text-indigo-600 hover:text-indigo-800"
              onClick={() => navigate('/')}
            >
              <span>Continue Shopping</span>
              <FiChevronRight className="ml-1" />
            </button>
          </div>

          {wishlist.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow-sm">
              <div className="mx-auto h-24 w-24 text-gray-400 flex items-center justify-center">
                <AiOutlineHeart className="h-full w-full" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Your wishlist is empty</h3>
              <p className="mt-2 text-sm text-gray-500">
                Start adding items you love by clicking the heart icon
              </p>
              <div className="mt-6">
                <button 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" 
                  onClick={() => navigate('/')}
                >
                  Browse Products
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {wishlist.map((item) => {
                const product = item.productId;
                const variant = product.variants?.find(v => v._id === item.variantId) || product.variants?.[0];
                const ratings = productRatings[product._id] || [];
                const avgRating = calculateAverageRating(ratings);
                const reviewCount = ratings.length;
                const inStock = variant ? variant.stock > 0 : product.stock > 0;
                const productImage = getProductImage(product, variant);
                const variantAttributes = variant?.attributes?.map(attr => attr.value).join(' / ') || '';
                const uniqueItemId = `${product._id}-${item.variantId}`;

                return (
                  <div 
                    key={item._id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer flex flex-col h-full"
                    onClick={() => fetchProductDetails(product._id)}
                  >
                    <div className="relative aspect-square bg-gray-100">
                      <img
                        src={productImage}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(product._id)}
                      />
                      {!inStock && (
                        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
                          <span className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm font-medium">
                            Out of Stock
                          </span>
                        </div>
                      )}
                      <button
                        onClick={(e) => handleRemoveFromWishlist(product._id, item.variantId, e)}
                        disabled={removingId === uniqueItemId}
                        className="absolute top-2 right-2 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all duration-200 shadow-sm"
                      >
                        {removingId === uniqueItemId ? (
                          <div className="h-5 w-5 flex items-center justify-center">
                            <PulseLoader size={6} color="#EF4444" />
                          </div>
                        ) : (
                          <FiTrash2 className="h-5 w-5 text-red-500 hover:text-red-700" />
                        )}
                      </button>
                      {product.promotion && (
                        <div className="absolute top-2 left-2 bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full flex items-center">
                          <BsStars className="mr-1" />
                          <span>Deal</span>
                        </div>
                      )}
                    </div>

                    <div className="p-4 flex flex-col flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">
                        {product.name}
                      </h3>
                      {variantAttributes && (
                        <p className="text-sm text-gray-500 mb-1">
                          {variantAttributes}
                        </p>
                      )}
                      <p className="text-gray-600 text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
                        {product.description || 'No description available'}
                      </p>
                      
                      <div className="flex items-center mb-2">
                        <div className="flex mr-1">
                          {renderRatingStars(avgRating)}
                        </div>
                        <span className="text-xs text-gray-500">
                          ({reviewCount})
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center mt-auto">
                        <div>
                          <span className="text-lg font-bold text-blue-600">
                            ₹{variant?.price?.toFixed(2) || product.price?.toFixed(2) || '0.00'}
                          </span>
                          {variant?.mrp && variant.mrp > variant.price && (
                            <span className="text-xs text-gray-500 line-through ml-1">
                              ₹{variant.mrp.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </div>
                      
                      <div className="mt-4">
                        {renderProductActions(product, variant)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
      </div>
      <Footer />
    </>
  );
};

export default WishlistPage;